import * as RadixPopover from "@radix-ui/react-popover";
import { CaretDown, Check, X } from "@phosphor-icons/react";
import { forwardRef, Fragment, type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import {
  floatingCollisionPadding,
  floatingLayerClassName,
  multiSelectListboxCollisionClassName,
  searchableSelectContentCollisionClassName
} from "@/components/ui/overlays/overlayPositioning";
import { Button, type ButtonVariant } from "@/components/primitives/Button";
import { Spinner } from "../../feedback/Spinner";
import { groupSelectOptions, type SelectMenuWidth, type SelectOption } from "../Select";

/**
 * When the value is committed back to the consumer via `onValueChange`:
 * - `"change"` (default) — on every toggle, immediately.
 * - `"apply"` — only when the user confirms with the Apply button (or a custom
 *   footer action). Toggles update an internal draft; closing without applying
 *   discards the draft. A Clear/Apply footer is shown automatically.
 */
export type MultiSelectCommitMode = "change" | "apply";

export type MultiSelectProps = {
  options: SelectOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  /** Fired with the committed value when the user applies (apply mode only). */
  onApply?: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  size?: "sm" | "md";
  maxVisibleValues?: number;
  /** Option popover width relative to the trigger. Defaults to `"auto"` (the
   * popover grows to fit its content and can be wider than the trigger). */
  menuWidth?: SelectMenuWidth;
  /** Defer committing the value until applied. Defaults to `"change"`. */
  commitMode?: MultiSelectCommitMode;
  /** Custom popover footer. Overrides the default Clear/Apply footer. */
  footer?: ReactNode;
  applyLabel?: string;
  clearLabel?: string;
  /** Button variant for the default footer Apply action. Defaults to `"primary"`. */
  applyVariant?: ButtonVariant;
  /** Button variant for the default footer Clear action. Defaults to `"ghost"`. */
  clearVariant?: ButtonVariant;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

const uniqueValues = (values: string[]) => Array.from(new Set(values));

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value,
      defaultValue = [],
      onValueChange,
      onApply,
      placeholder = "Select",
      searchPlaceholder = "Search...",
      emptyText = "No results found",
      loading,
      disabled,
      error,
      clearable = true,
      size = "md",
      maxVisibleValues = 2,
      menuWidth = "auto",
      commitMode = "change",
      footer,
      applyLabel = "Apply",
      clearLabel = "Clear",
      applyVariant = "primary",
      clearVariant = "ghost",
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const isApplyMode = commitMode === "apply";
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(() => uniqueValues(defaultValue));
    // Draft holds the in-progress selection in apply mode; the committed value is
    // only written when the user applies.
    const [draft, setDraft] = useState<string[]>(() => uniqueValues(defaultValue));
    const inputRef = useRef<HTMLInputElement>(null);

    const committedValues = useMemo(() => uniqueValues(value ?? uncontrolledValue), [value, uncontrolledValue]);
    // Values reflected by the option checkmarks: the draft while applying, else committed.
    const workingValues = isApplyMode ? draft : committedValues;
    const workingSet = useMemo(() => new Set(workingValues), [workingValues]);

    const optionMap = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
    const selectedOptions = committedValues.map((selectedValue) => optionMap.get(selectedValue) ?? { value: selectedValue, label: selectedValue });
    const visibleSelectedOptions = selectedOptions.slice(0, Math.max(maxVisibleValues, 0));
    const hiddenSelectedCount = Math.max(selectedOptions.length - visibleSelectedOptions.length, 0);
    const filteredOptions = useMemo(
      () => options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())),
      [options, query]
    );
    const filteredGroups = useMemo(() => groupSelectOptions(filteredOptions), [filteredOptions]);

    // Keep the draft synced to the committed value whenever the popover is closed,
    // so opening always starts from the current committed selection.
    useEffect(() => {
      if (!open) setDraft(committedValues);
    }, [open, committedValues]);

    const writeValue = (nextValue: string[]) => {
      const normalizedValue = uniqueValues(nextValue);
      if (value === undefined) setUncontrolledValue(normalizedValue);
      onValueChange?.(normalizedValue);
    };

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setDraft(committedValues);
      } else {
        // Closing without applying discards the draft (handled by the effect above).
        setQuery("");
        setActiveIndex(0);
      }
    };

    const toggleValue = (nextValue: string) => {
      const next = workingSet.has(nextValue)
        ? workingValues.filter((selectedValue) => selectedValue !== nextValue)
        : [...workingValues, nextValue];

      if (isApplyMode) {
        setDraft(uniqueValues(next));
      } else {
        writeValue(next);
      }
    };

    const applyDraft = () => {
      writeValue(draft);
      onApply?.(uniqueValues(draft));
      handleOpenChange(false);
    };

    const clearSelection = () => {
      if (isApplyMode) {
        setDraft([]);
      } else {
        writeValue([]);
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (!open && ["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        handleOpenChange(true);
        return;
      }

      if (!open) return;

      const eventTarget = event.target instanceof HTMLElement ? event.target : null;
      if (event.key === "Enter" && eventTarget?.closest("[data-bfrs-multi-select-option]")) {
        return;
      }

      if (event.key === "Escape") handleOpenChange(false);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Enter" && filteredOptions[activeIndex] && !filteredOptions[activeIndex].disabled) {
        event.preventDefault();
        toggleValue(filteredOptions[activeIndex].value);
      }
    };

    const hasSelection = selectedOptions.length > 0;
    const menuWidthClass =
      menuWidth === "trigger"
        ? "bfrs-w-[var(--radix-popover-trigger-width)]"
        : typeof menuWidth === "number"
          ? ""
          : "bfrs-min-w-[var(--radix-popover-trigger-width)]";
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const showDefaultFooter = isApplyMode && footer === undefined;

    const renderOption = (option: SelectOption, index: number) => {
      const selected = workingSet.has(option.value);
      return (
        <button
          key={option.value}
          type="button"
          role="option"
          data-bfrs-multi-select-option=""
          aria-selected={selected}
          disabled={option.disabled}
          className={cn(
            "bfrs-flex bfrs-w-full bfrs-cursor-pointer bfrs-items-center bfrs-gap-2.5 bfrs-rounded-sm bfrs-px-3 bfrs-py-2 bfrs-text-left bfrs-text-[12px] bfrs-text-text-primary bfrs-outline-none hover:bfrs-bg-primary-soft disabled:bfrs-cursor-not-allowed disabled:bfrs-text-text-disabled",
            selected && "bfrs-bg-primary-soft bfrs-font-bold",
            activeIndex === index && "bfrs-bg-primary-soft"
          )}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => toggleValue(option.value)}
        >
          <span
            className={cn(
              "bfrs-flex bfrs-size-4 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-sm bfrs-border",
              selected ? "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast" : "bfrs-border-border-control bfrs-bg-surface-card"
            )}
            aria-hidden="true"
          >
            {selected && <Check size={12} weight="bold" />}
          </span>
          <span className="bfrs-min-w-0 bfrs-flex-1 bfrs-truncate">{option.label}</span>
        </button>
      );
    };

    // Render grouped options while keeping a single flat index for keyboard nav.
    let flatIndex = -1;

    return (
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            data-filled={hasSelection && !error ? "" : undefined}
            className={cn(
              "bfrs-flex bfrs-w-full bfrs-items-center bfrs-justify-between bfrs-gap-2 bfrs-rounded-lg bfrs-border bfrs-bg-surface-card bfrs-text-left bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20 data-[state=open]:bfrs-ring-2 data-[state=open]:bfrs-ring-border-focus/20 aria-[expanded=true]:bfrs-ring-2 aria-[expanded=true]:bfrs-ring-border-focus/20 disabled:bfrs-cursor-not-allowed disabled:bfrs-bg-surface-app disabled:bfrs-text-text-disabled",
              cn("bfrs-rounded-[7px] bfrs-px-3 bfrs-text-[12px]", size === "sm" ? "bfrs-h-7" : "bfrs-h-9"),
              error
                ? "bfrs-border-danger focus-visible:bfrs-border-danger focus-visible:bfrs-ring-danger/15 data-[state=open]:bfrs-border-danger data-[state=open]:bfrs-ring-danger/15 aria-[expanded=true]:bfrs-border-danger aria-[expanded=true]:bfrs-ring-danger/15"
                : cn("bfrs-border-border focus-visible:bfrs-border-border-focus data-[state=open]:bfrs-border-border-focus aria-[expanded=true]:bfrs-border-border-focus", size === "sm" && "data-[filled]:bfrs-border-border-focus"),
              hasSelection ? "bfrs-text-text-primary" : "bfrs-text-text-placeholder",
              className
            )}
            onKeyDown={handleKeyDown}
          >
            <span className="bfrs-flex bfrs-min-w-0 bfrs-flex-1 bfrs-items-center bfrs-gap-1.5 bfrs-overflow-hidden">
              {hasSelection ? (
                <>
                  {visibleSelectedOptions.map((option) => (
                    <span
                      key={option.value}
                      className="bfrs-inline-flex bfrs-max-w-[9rem] bfrs-shrink-0 bfrs-items-center bfrs-rounded-pill bfrs-bg-primary-soft bfrs-px-2 bfrs-py-0.5 bfrs-text-xs bfrs-font-medium bfrs-text-primary"
                    >
                      <span className="bfrs-truncate">{option.label}</span>
                    </span>
                  ))}
                  {hiddenSelectedCount > 0 && (
                    <span className="bfrs-shrink-0 bfrs-rounded-pill bfrs-bg-surface-raised bfrs-px-2 bfrs-py-0.5 bfrs-text-xs bfrs-font-semibold bfrs-text-text-secondary">
                      +{hiddenSelectedCount}
                    </span>
                  )}
                </>
              ) : (
                <span className="bfrs-truncate">{placeholder}</span>
              )}
            </span>
            <span className="bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-1 bfrs-text-text-muted">
              {clearable && hasSelection && (
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear selections"
                  className="bfrs-rounded-sm bfrs-p-0.5 hover:bfrs-bg-surface-muted"
                  onClick={(event) => {
                    event.stopPropagation();
                    writeValue([]);
                    setDraft([]);
                  }}
                >
                  <X size={14} />
                </span>
              )}
              <CaretDown className={cn("bfrs-transition-transform bfrs-duration-150", open && "bfrs-rotate-180")} size={10} />
            </span>
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <RadixPopover.Content
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              style={menuWidthStyle}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                inputRef.current?.focus();
              }}
              onKeyDown={handleKeyDown}
              className={cn(
                floatingLayerClassName,
                "bfrs-flex bfrs-flex-col bfrs-rounded-lg bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-p-1 bfrs-shadow-elevated bfrs-outline-none bfrs-animate-content-in",
                menuWidthClass,
                searchableSelectContentCollisionClassName
              )}
            >
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder={searchPlaceholder}
                className="bfrs-mb-1 bfrs-h-7 bfrs-w-full bfrs-shrink-0 bfrs-rounded-[7px] bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-px-3 bfrs-text-[12px] bfrs-text-text-primary placeholder:bfrs-text-text-placeholder bfrs-outline-none focus:bfrs-border-border-focus focus:bfrs-ring-2 focus:bfrs-ring-border-focus/20 focus-visible:bfrs-border-border-focus focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20"
              />
              <div role="listbox" aria-multiselectable="true" className={multiSelectListboxCollisionClassName}>
                {loading ? (
                  <div className="bfrs-flex bfrs-h-16 bfrs-items-center bfrs-justify-center bfrs-text-text-muted">
                    <Spinner size="sm" />
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <div className="bfrs-px-3 bfrs-py-4 bfrs-text-sm bfrs-text-text-muted">{emptyText}</div>
                ) : (
                  filteredGroups.map((grp, groupIndex) => (
                    <Fragment key={`group-${groupIndex}`}>
                      {grp.group && (
                        <div className="bfrs-px-3 bfrs-pb-1 bfrs-pt-2 bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.08em] bfrs-text-text-muted">
                          {grp.group}
                        </div>
                      )}
                      {grp.items.map((option) => {
                        flatIndex += 1;
                        return renderOption(option, flatIndex);
                      })}
                    </Fragment>
                  ))
                )}
              </div>
              {footer !== undefined ? (
                <div className="bfrs-mt-1 bfrs-shrink-0 bfrs-border-t bfrs-border-border bfrs-pt-1">{footer}</div>
              ) : showDefaultFooter ? (
                <div className="bfrs-mt-1 bfrs-flex bfrs-shrink-0 bfrs-items-center bfrs-justify-between bfrs-gap-2 bfrs-border-t bfrs-border-border bfrs-px-1 bfrs-pb-1 bfrs-pt-2">
                  <Button type="button" variant={clearVariant} size="xs" onClick={clearSelection}>
                    {clearLabel}
                  </Button>
                  <Button type="button" variant={applyVariant} size="xs" onClick={applyDraft}>
                    {applyLabel}
                  </Button>
                </div>
              ) : null}
            </RadixPopover.Content>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
